import { useCallback, useRef, useState } from 'react'

import { useI18N } from '@susu/i18n/client'
import { Icon, Label } from '@susu/ui/components'
import { useQuery } from '@tanstack/react-query'

import {
  enrichItemsInCartItems,
  type ItemsInCartApiItem,
} from '../api/itemsInCartApi'
import { useCopyProductCode } from '../hooks/useCopyProductCode'
import { useNbaCard } from '../hooks/useNbaCard'
import { cn } from '../lib/utils'

import NbaFeedbackSheet from './NbaFeedbackSheet'

import type {
  NbaFeedbackScore,
  NbaProduct,
  NbaProductReason,
} from '../api/nbaCardApi'
import type { EnvironmentWithChina } from '../App'
import type { IconProps } from '@susu/ui/components'

const MAX_BULLETS = 3
// One row of tiles: the card is too narrow for a second one to read well.
const MAX_PRODUCTS = 4

const PRODUCT_REASON_ICONS: Record<NbaProductReason, IconProps['name']> = {
  purchased: 'bag',
  returned: 'return-arrow',
  wishlist: 'wishlist-outline',
  cart: 'cart',
  browsed: 'show',
}

export interface NbaFeedback {
  cardId: string
  rating: NbaFeedbackScore
  notes: string
}

interface NbaCardProps {
  accountNumber: string
  authToken?: string
  env: EnvironmentWithChina
  /** `NBA_cards_final` GrowthBook flag — off means the card never renders. */
  isEnabled: boolean
  /**
   * The associate already rated this card in the current profile visit — the
   * footer shows the thank-you line instead of the thumbs. Owned by the
   * caller so it survives tab switches but resets when the profile reopens.
   */
  hasSentFeedback?: boolean
  onFeedback?: (feedback: NbaFeedback) => void
}

/**
 * Next Best Action card — in-store only, behind the `NBA_cards_final`
 * GrowthBook flag (RP-6063), with a Beta label and thumbs up/down feedback
 * in the footer (RP-6164).
 *
 * Design: locked handoff spec (design_handoff_next_best_action) — hairline
 * border, white card, no title, the `recommended` icon inline with the
 * headline. That icon renders in its own brand-gradient fill regardless of
 * the design system's `color` prop — the one intentional departure from the
 * card's otherwise monochrome styling, signalling "AI-generated".
 *
 * Items the card refers to render as a row of tiles under the bullets, each
 * tagged with why it is relevant (purchased, returned, wishlist, cart,
 * browsed). The trait only carries style codes; images and names come from
 * the product API, so the tiles show their tag first and fill in once that
 * request lands.
 */
export default function NbaCard({
  accountNumber,
  authToken,
  env,
  isEnabled,
  hasSentFeedback = false,
  onFeedback,
}: Readonly<NbaCardProps>) {
  const { t } = useI18N()
  const { data: card, isError } = useNbaCard({
    accountNumber,
    env,
    authToken,
    enabled: isEnabled,
  })
  const [pendingRating, setPendingRating] = useState<NbaFeedbackScore | null>(
    null,
  )
  // Read synchronously so a double tap on Send reports once, even before the
  // re-render that unmounts the sheet.
  const pendingRatingRef = useRef<NbaFeedbackScore | null>(null)
  const cardId = card?.cardId ?? ''
  const products = (card?.products ?? []).slice(0, MAX_PRODUCTS)
  const productCodes = products.map((product) => product.itemCode)
  const { data: productDetails } = useQuery({
    queryKey: ['nbaCard', 'products', accountNumber, env, productCodes],
    queryFn: () =>
      enrichItemsInCartItems(
        productCodes.map((identifier) => ({ identifier })),
        accountNumber,
        env,
        authToken,
      ),
    enabled: isEnabled && productCodes.length > 0,
    staleTime: 60 * 1000,
  })

  const openSheet = (rating: NbaFeedbackScore) => {
    pendingRatingRef.current = rating
    setPendingRating(rating)
  }

  const handleSheetFinish = useCallback(
    (notes: string) => {
      const rating = pendingRatingRef.current
      if (!rating) return
      pendingRatingRef.current = null
      setPendingRating(null)
      onFeedback?.({ cardId, rating, notes })
    },
    [cardId, onFeedback],
  )

  if (!isEnabled || isError || !card) {
    return null
  }

  const bullets = card.bullets.slice(0, MAX_BULLETS)

  return (
    <section
      className="flex flex-col gap-10 rounded-md border border-border bg-surface p-16 max-lg:mx-16"
      data-testid="nba-card"
    >
      <div className="flex items-start gap-10">
        <Icon name="recommended" size="large" className="shrink-0" />
        <h2
          className="text-base leading-base font-medium tracking-heading text-text [text-wrap:pretty]"
          data-testid="nba-card-headline"
        >
          {card.action}
        </h2>
      </div>
      <ul role="list" className="flex list-none flex-col gap-4 pl-[34px]">
        {bullets.map((bullet, index) => (
          <li key={index} className="flex gap-8" data-testid="nba-card-bullet">
            <span
              aria-hidden="true"
              className="mt-[8px] h-3 w-3 shrink-0 rounded-full bg-text"
            />
            <span className="text-sm leading-sm font-light text-text [text-wrap:pretty]">
              {bullet}
            </span>
          </li>
        ))}
      </ul>
      {products.length > 0 && (
        <ul
          role="list"
          className="grid list-none grid-cols-4 gap-8 pt-14 pb-6"
          data-testid="nba-card-products"
        >
          {products.map((product, index) => (
            <NbaProductTile
              key={product.itemCode}
              product={product}
              details={productDetails?.[index]}
            />
          ))}
        </ul>
      )}
      <div
        className="flex min-h-44 items-center justify-between gap-8 border-t border-border pt-12"
        data-testid="nba-card-footer"
      >
        <Label
          size="s"
          color="grey"
          className="!text-text-secondary"
          text={t('nbaCard.beta')}
        />
        {hasSentFeedback ? (
          <p
            className="text-sm leading-sm font-light text-text-secondary"
            data-testid="nba-card-feedback-thanks"
          >
            {t('nbaCard.thanks')}
          </p>
        ) : (
          <div className="-my-12 -mr-12 flex items-center">
            <span className="mr-4 text-sm leading-sm font-light text-text-secondary">
              {t('nbaCard.useful')}
            </span>
            <button
              type="button"
              aria-label={t('nbaCard.ratePositive')}
              className="flex h-44 w-44 items-center justify-center rounded-full hover:bg-surface-hover"
              data-testid="nba-card-feedback-up"
              onClick={() => openSheet('good')}
            >
              <Icon name="thumbs-up" size={20} color="text" />
            </button>
            <button
              type="button"
              aria-label={t('nbaCard.rateNegative')}
              className="flex h-44 w-44 items-center justify-center rounded-full hover:bg-surface-hover"
              data-testid="nba-card-feedback-down"
              onClick={() => openSheet('bad')}
            >
              <Icon name="thumbs-down" size={20} color="text" />
            </button>
          </div>
        )}
      </div>
      {pendingRating && (
        <NbaFeedbackSheet rating={pendingRating} onFinish={handleSheetFinish} />
      )}
    </section>
  )
}

interface NbaProductTileProps {
  product: NbaProduct
  details?: ItemsInCartApiItem
}

function NbaProductTile({ product, details }: Readonly<NbaProductTileProps>) {
  const { t } = useI18N()
  const [imageError, setImageError] = useState(false)
  const { canCopy, clickableProps } = useCopyProductCode(product.itemCode)
  const imageUrl = details?.imageUrl?.trim()
  const name =
    details?.commercialDescription?.trim() ||
    details?.CommercialDescription?.trim() ||
    product.itemCode

  return (
    <li
      className="relative min-w-0"
      data-testid="nba-card-product"
      data-reason={product.reason}
    >
      <div
        className={cn(
          'flex aspect-[4/5] items-center justify-center overflow-hidden rounded-sm bg-gray-200 p-14',
          canCopy && 'cursor-pointer',
        )}
        {...clickableProps}
      >
        {imageUrl && !imageError && (
          <img
            src={imageUrl}
            alt={name}
            loading="lazy"
            decoding="async"
            onError={() => setImageError(true)}
            className="h-full w-full object-contain object-center"
          />
        )}
      </div>
      <span
        className="backdrop-blur-sm pointer-events-none absolute top-6 left-6 flex h-22 items-center gap-4 rounded-md bg-white-alpha-600 pr-6 pl-4 text-xs leading-xs font-light text-text"
        data-testid="nba-card-product-reason"
      >
        <Icon name={PRODUCT_REASON_ICONS[product.reason]} size="extra-small" />
        {t(`nbaCard.products.${product.reason}`)}
      </span>
    </li>
  )
}
